import React, { useState, useEffect } from 'react';
import { Mail, Server, Send, Check, X, Loader2, Eye, EyeOff, AlertCircle } from 'lucide-react';
import apiService from '../services/api';
import { useToast } from '../context/ToastContext';

interface EmailConfig {
  provider: string;
  config: Record<string, any> | null;
  from_address: string | null;
  from_name: string | null;
  reply_to: string | null;
  verified: boolean;
  verified_at: string | null;
}

interface Props {
  agencyId: string;
  agencyName: string;
}

type Provider = 'default' | 'smtp' | 'sendgrid' | 'mailgun' | 'postmark';

const PROVIDERS: { value: Provider; label: string; description: string }[] = [
  { value: 'default', label: 'Default (DashboardFox)', description: 'Use DashboardFox email service' },
  { value: 'smtp', label: 'SMTP', description: 'Custom SMTP server' },
  { value: 'sendgrid', label: 'SendGrid', description: 'SendGrid API' },
  { value: 'mailgun', label: 'Mailgun', description: 'Mailgun API' },
  { value: 'postmark', label: 'Postmark', description: 'Postmark API' }
];

const AgencyEmailConfigTab: React.FC<Props> = ({ agencyId, agencyName }) => {
  const { showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [config, setConfig] = useState<EmailConfig | null>(null);
  const [provider, setProvider] = useState<Provider>('default');
  const [providerConfig, setProviderConfig] = useState<Record<string, string>>({});
  const [fromAddress, setFromAddress] = useState('');
  const [fromName, setFromName] = useState('');
  const [replyTo, setReplyTo] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [focusedInput, setFocusedInput] = useState<string | null>(null);
  const [hoveredButton, setHoveredButton] = useState<string | null>(null);

  useEffect(() => {
    loadConfig();
  }, [agencyId]);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const response = await apiService.get(`/agencies/${agencyId}/email-config`);
      setConfig(response.data);
      setProvider(response.data.provider || 'default');
      setFromAddress(response.data.from_address || '');
      setFromName(response.data.from_name || '');
      setReplyTo(response.data.reply_to || '');
      
      // Load provider config (masked values)
      if (response.data.config) {
        setProviderConfig(response.data.config);
      }
    } catch (err: any) {
      showError(err.response?.data?.error || 'Failed to load email configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      const payload: Record<string, any> = {
        provider,
        from_address: fromAddress || null,
        from_name: fromName || null,
        reply_to: replyTo || null
      };

      if (provider !== 'default') {
        // Only include non-masked values
        const cleanConfig: Record<string, string> = {};
        for (const [key, value] of Object.entries(providerConfig)) {
          if (value && !value.includes('••••')) {
            cleanConfig[key] = value;
          }
        }
        if (Object.keys(cleanConfig).length > 0) {
          payload.config = cleanConfig;
        }
      }

      await apiService.put(`/agencies/${agencyId}/email-config`, payload);
      showSuccess('Email configuration saved successfully');
      loadConfig();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Failed to save email configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!testEmail) {
      showError('Please enter a test email address');
      return;
    }

    try {
      setTesting(true);
      const response = await apiService.post(`/agencies/${agencyId}/email-config/test`, {
        send_test_email: true,
        test_email_address: testEmail
      });
      
      if (response.data.success) {
        showSuccess(response.data.message || 'Test email sent successfully!');
        loadConfig(); // Refresh to show verified status
      } else {
        showError(response.data.message || 'Test failed');
      }
    } catch (err: any) {
      showError(err.response?.data?.error || err.response?.data?.message || 'Failed to send test email');
    } finally {
      setTesting(false);
    }
  };

  const inputStyle = (focused: boolean): React.CSSProperties => ({
    width: '100%',
    padding: '10px 12px',
    backgroundColor: 'var(--v2-surface)',
    border: `1px solid ${focused ? 'var(--v2-primary)' : 'var(--v2-border)'}`,
    borderRadius: 'var(--radius-sm)',
    fontSize: '0.875rem',
    color: 'var(--v2-text)',
    outline: 'none',
    boxSizing: 'border-box' as const,
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
    boxShadow: focused ? '0 0 0 3px var(--v2-primary-muted)' : 'none'
  });

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: 500,
    color: 'var(--v2-text)',
    marginBottom: '6px'
  };

  const buttonStyle = (hovered: boolean, variant: 'primary' | 'secondary' | 'success' = 'primary'): React.CSSProperties => ({
    padding: '10px 20px',
    backgroundColor: variant === 'primary'
      ? hovered ? 'var(--v2-primary-hover)' : 'var(--v2-primary)'
      : variant === 'success'
        ? hovered ? '#059669' : '#10b981'
        : hovered ? 'var(--v2-surface-hover)' : 'var(--v2-surface)',
    color: variant === 'secondary' ? 'var(--v2-text)' : '#ffffff',
    border: variant === 'secondary' ? '1px solid var(--v2-border)' : 'none',
    borderRadius: 'var(--radius-md)',
    fontSize: '0.875rem',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.15s',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px'
  });

  const renderProviderConfig = () => {
    switch (provider) {
      case 'smtp':
        return (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <label style={labelStyle}>SMTP Host</label>
              <input
                type="text"
                value={providerConfig.host || ''}
                onChange={(e) => setProviderConfig({ ...providerConfig, host: e.target.value })}
                onFocus={() => setFocusedInput('host')}
                onBlur={() => setFocusedInput(null)}
                placeholder="smtp.example.com"
                style={inputStyle(focusedInput === 'host')}
              />
            </div>
            <div>
              <label style={labelStyle}>Port</label>
              <input
                type="number"
                value={providerConfig.port || ''}
                onChange={(e) => setProviderConfig({ ...providerConfig, port: e.target.value })}
                onFocus={() => setFocusedInput('port')}
                onBlur={() => setFocusedInput(null)}
                placeholder="587"
                style={inputStyle(focusedInput === 'port')}
              />
            </div>
            <div>
              <label style={labelStyle}>Username</label>
              <input
                type="text"
                value={providerConfig.username || ''}
                onChange={(e) => setProviderConfig({ ...providerConfig, username: e.target.value })}
                onFocus={() => setFocusedInput('username')}
                onBlur={() => setFocusedInput(null)}
                placeholder="user@example.com"
                style={inputStyle(focusedInput === 'username')}
              />
            </div>
            <div>
              <label style={labelStyle}>Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={providerConfig.password || ''}
                  onChange={(e) => setProviderConfig({ ...providerConfig, password: e.target.value })}
                  onFocus={() => setFocusedInput('password')}
                  onBlur={() => setFocusedInput(null)}
                  placeholder={providerConfig.password_set ? '••••••••' : 'Enter password'}
                  style={{ ...inputStyle(focusedInput === 'password'), paddingRight: '40px' }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: 'var(--v2-text-muted)'
                  }}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
          </div>
        );

      case 'sendgrid':
        return (
          <div>
            <label style={labelStyle}>API Key</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={providerConfig.apiKey || ''}
                onChange={(e) => setProviderConfig({ ...providerConfig, apiKey: e.target.value })}
                onFocus={() => setFocusedInput('apiKey')}
                onBlur={() => setFocusedInput(null)}
                placeholder={providerConfig.apiKey_set ? '••••••••' : 'SG.xxxxxx'}
                style={{ ...inputStyle(focusedInput === 'apiKey'), paddingRight: '40px' }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--v2-text-muted)'
                }}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
        );

      case 'mailgun':
        return (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <label style={labelStyle}>API Key</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={providerConfig.apiKey || ''}
                  onChange={(e) => setProviderConfig({ ...providerConfig, apiKey: e.target.value })}
                  onFocus={() => setFocusedInput('apiKey')}
                  onBlur={() => setFocusedInput(null)}
                  placeholder={providerConfig.apiKey_set ? '••••••••' : 'key-xxxxxx'}
                  style={{ ...inputStyle(focusedInput === 'apiKey'), paddingRight: '40px' }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: 'var(--v2-text-muted)'
                  }}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div>
              <label style={labelStyle}>Domain</label>
              <input
                type="text"
                value={providerConfig.domain || ''}
                onChange={(e) => setProviderConfig({ ...providerConfig, domain: e.target.value })}
                onFocus={() => setFocusedInput('domain')}
                onBlur={() => setFocusedInput(null)}
                placeholder="mg.example.com"
                style={inputStyle(focusedInput === 'domain')}
              />
            </div>
            <div>
              <label style={labelStyle}>Region</label>
              <select
                value={providerConfig.region || 'US'}
                onChange={(e) => setProviderConfig({ ...providerConfig, region: e.target.value })}
                style={{ ...inputStyle(false), cursor: 'pointer' }}
              >
                <option value="US">US</option>
                <option value="EU">EU</option>
              </select>
            </div>
          </div>
        );

      case 'postmark':
        return (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <label style={labelStyle}>Server API Token</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={providerConfig.apiToken || ''}
                  onChange={(e) => setProviderConfig({ ...providerConfig, apiToken: e.target.value })}
                  onFocus={() => setFocusedInput('apiToken')}
                  onBlur={() => setFocusedInput(null)}
                  placeholder={providerConfig.apiToken_set ? '••••••••' : 'xxxxxxxx-xxxx-xxxx-xxxx'}
                  style={{ ...inputStyle(focusedInput === 'apiToken'), paddingRight: '40px' }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: 'var(--v2-text-muted)'
                  }}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div>
              <label style={labelStyle}>Message Stream ID</label>
              <input
                type="text"
                value={providerConfig.messageStreamId || ''}
                onChange={(e) => setProviderConfig({ ...providerConfig, messageStreamId: e.target.value })}
                onFocus={() => setFocusedInput('messageStreamId')}
                onBlur={() => setFocusedInput(null)}
                placeholder="outbound"
                style={inputStyle(focusedInput === 'messageStreamId')}
              />
            </div>
          </div>
        );

      default:
        return (
          <div style={{
            padding: '20px',
            backgroundColor: 'var(--v2-primary-light)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--v2-primary-muted)'
          }}>
            <p style={{ color: 'var(--v2-primary)', margin: 0, fontSize: '0.875rem' }}>
              Using DashboardFox's default email service. Emails will be sent from noreply@email.dashboardfox.com unless you customize the sender below.
            </p>
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <Loader2 size={24} strokeWidth={2} style={{ animation: 'spin 1s linear infinite' }} color="var(--v2-primary)" />
        <p style={{ color: 'var(--v2-text-tertiary)', marginTop: '12px' }}>Loading email configuration...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Provider Selection */}
      <div style={{
        backgroundColor: 'var(--v2-surface)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--v2-border)',
        padding: '24px',
        marginBottom: '20px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
          <Server size={20} strokeWidth={1.5} color="var(--v2-primary)" />
          <h3 style={{ fontSize: '1rem', fontWeight: 500, color: 'var(--v2-text)', margin: 0 }}>
            Email Provider
          </h3>
          {config?.verified && (
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              padding: '4px 8px',
              backgroundColor: 'var(--v2-success-bg)',
              color: 'var(--v2-success)',
              borderRadius: 'var(--radius-full)',
              fontSize: '0.75rem',
              fontWeight: 500
            }}>
              <Check size={12} /> Verified
            </span>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '12px', marginBottom: '20px' }}>
          {PROVIDERS.map((p) => (
            <button
              key={p.value}
              onClick={() => {
                setProvider(p.value);
                setProviderConfig({});
              }}
              style={{
                padding: '16px 12px',
                backgroundColor: provider === p.value ? 'var(--v2-primary-light)' : 'var(--v2-surface)',
                border: `2px solid ${provider === p.value ? 'var(--v2-primary)' : 'var(--v2-border)'}`,
                borderRadius: 'var(--radius-md)',
                cursor: 'pointer',
                transition: 'all 0.15s',
                textAlign: 'center'
              }}
            >
              <p style={{
                margin: 0,
                fontSize: '0.875rem',
                fontWeight: 500,
                color: provider === p.value ? 'var(--v2-primary)' : 'var(--v2-text)'
              }}>
                {p.label}
              </p>
              <p style={{
                margin: '4px 0 0 0',
                fontSize: '0.875rem',
                color: 'var(--v2-text-muted)'
              }}>
                {p.description}
              </p>
            </button>
          ))}
        </div>

        {/* Provider-specific config */}
        {renderProviderConfig()}
      </div>

      {/* Sender Settings */}
      <div style={{
        backgroundColor: 'var(--v2-surface)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--v2-border)',
        padding: '24px',
        marginBottom: '20px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
          <Mail size={20} strokeWidth={1.5} color="var(--v2-primary)" />
          <h3 style={{ fontSize: '1rem', fontWeight: 500, color: 'var(--v2-text)', margin: 0 }}>
            Sender Settings
          </h3>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
          <div>
            <label style={labelStyle}>From Email Address</label>
            <input
              type="email"
              value={fromAddress}
              onChange={(e) => setFromAddress(e.target.value)}
              onFocus={() => setFocusedInput('fromAddress')}
              onBlur={() => setFocusedInput(null)}
              placeholder="noreply@yourdomain.com"
              style={inputStyle(focusedInput === 'fromAddress')}
            />
          </div>
          <div>
            <label style={labelStyle}>From Name</label>
            <input
              type="text"
              value={fromName}
              onChange={(e) => setFromName(e.target.value)}
              onFocus={() => setFocusedInput('fromName')}
              onBlur={() => setFocusedInput(null)}
              placeholder={agencyName}
              style={inputStyle(focusedInput === 'fromName')}
            />
          </div>
          <div>
            <label style={labelStyle}>Reply-To Address</label>
            <input
              type="email"
              value={replyTo}
              onChange={(e) => setReplyTo(e.target.value)}
              onFocus={() => setFocusedInput('replyTo')}
              onBlur={() => setFocusedInput(null)}
              placeholder="support@yourdomain.com"
              style={inputStyle(focusedInput === 'replyTo')}
            />
          </div>
        </div>
      </div>

      {/* Test Connection */}
      <div style={{
        backgroundColor: 'var(--v2-surface)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--v2-border)',
        padding: '24px',
        marginBottom: '20px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
          <Send size={20} strokeWidth={1.5} color="var(--v2-primary)" />
          <h3 style={{ fontSize: '1rem', fontWeight: 500, color: 'var(--v2-text)', margin: 0 }}>
            Test Configuration
          </h3>
        </div>

        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Send Test Email To</label>
            <input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              onFocus={() => setFocusedInput('testEmail')}
              onBlur={() => setFocusedInput(null)}
              placeholder="your@email.com"
              style={inputStyle(focusedInput === 'testEmail')}
            />
          </div>
          <button
            onClick={handleTest}
            disabled={testing || !testEmail}
            onMouseEnter={() => setHoveredButton('test')}
            onMouseLeave={() => setHoveredButton(null)}
            style={{
              ...buttonStyle(hoveredButton === 'test', 'success'),
              opacity: (testing || !testEmail) ? 0.6 : 1,
              cursor: (testing || !testEmail) ? 'not-allowed' : 'pointer'
            }}
          >
            {testing ? (
              <Loader2 size={16} strokeWidth={2} style={{ animation: 'spin 1s linear infinite' }} />
            ) : (
              <Send size={16} strokeWidth={1.5} />
            )}
            {testing ? 'Sending...' : 'Send Test Email'}
          </button>
        </div>

        {!config?.verified && provider !== 'default' && (
          <div style={{
            marginTop: '16px',
            padding: '12px',
            backgroundColor: 'var(--v2-warning-bg)',
            borderRadius: 'var(--radius-md)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <AlertCircle size={16} color="#d97706" />
            <p style={{ margin: 0, fontSize: '0.8125rem', color: '#92400e' }}>
              Configuration not verified. Send a test email to verify your settings.
            </p>
          </div>
        )}
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
        <button
          onClick={handleSave}
          disabled={saving}
          onMouseEnter={() => setHoveredButton('save')}
          onMouseLeave={() => setHoveredButton(null)}
          style={{
            ...buttonStyle(hoveredButton === 'save', 'primary'),
            opacity: saving ? 0.6 : 1,
            cursor: saving ? 'not-allowed' : 'pointer'
          }}
        >
          {saving ? (
            <Loader2 size={16} strokeWidth={2} style={{ animation: 'spin 1s linear infinite' }} />
          ) : (
            <Check size={16} strokeWidth={1.5} />
          )}
          {saving ? 'Saving...' : 'Save Configuration'}
        </button>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default AgencyEmailConfigTab;
